import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Accordion as AccordionReactstrap,
} from 'reactstrap';
import styled, { keyframes } from 'styled-components';

const THEMES = {
  primary: {
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gradientLight: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
    shadowColor: 'rgba(102, 126, 234, 0.25)',
    iconBg: 'rgba(102, 126, 234, 0.1)',
    badgeBg: 'rgba(102, 126, 234, 0.2)',
    textColor: '#2d3748',
    iconColor: 'text-indigo-600',
  },
  secondary: {
    gradient: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)',
    gradientLight: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
    shadowColor: 'rgba(74, 85, 104, 0.2)',
    iconBg: 'rgba(74, 85, 104, 0.1)',
    badgeBg: 'rgba(74, 85, 104, 0.2)',
    textColor: '#1a202c',
    iconColor: 'text-gray-700',
  },
  success: {
    gradient: 'linear-gradient(135deg, #48bb78 0%, #276749 100%)',
    gradientLight: 'linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%)',
    shadowColor: 'rgba(72, 187, 120, 0.25)',
    iconBg: 'rgba(72, 187, 120, 0.1)',
    badgeBg: 'rgba(72, 187, 120, 0.2)',
    textColor: '#22543d',
    iconColor: 'text-green-600',
  },
  warning: {
    gradient: 'linear-gradient(135deg, #ed8936 0%, #c05621 100%)',
    gradientLight: 'linear-gradient(135deg, #fffaf0 0%, #fefcbf 100%)',
    shadowColor: 'rgba(237, 137, 54, 0.25)',
    iconBg: 'rgba(237, 137, 54, 0.1)',
    badgeBg: 'rgba(237, 137, 54, 0.2)',
    textColor: '#7b341e',
    iconColor: 'text-orange-600',
  },
  danger: {
    gradient: 'linear-gradient(135deg, #fc8181 0%, #c53030 100%)',
    gradientLight: 'linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)',
    shadowColor: 'rgba(252, 129, 129, 0.25)',
    iconBg: 'rgba(252, 129, 129, 0.1)',
    badgeBg: 'rgba(252, 129, 129, 0.2)',
    textColor: '#742a2a',
    iconColor: 'text-red-600',
  },
  info: {
    gradient: 'linear-gradient(135deg, #63b3ed 0%, #2b6cb0 100%)',
    gradientLight: 'linear-gradient(135deg, #ebf8ff 0%, #bee3f8 100%)',
    shadowColor: 'rgba(99, 179, 237, 0.25)',
    iconBg: 'rgba(99, 179, 237, 0.1)',
    badgeBg: 'rgba(99, 179, 237, 0.2)',
    textColor: '#2a4365',
    iconColor: 'text-blue-600',
  },
  muted: {
    gradient: 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)',
    gradientLight: 'linear-gradient(135deg, #f7fafc 0%, #e2e8f0 100%)',
    shadowColor: 'rgba(160, 174, 192, 0.2)',
    iconBg: 'rgba(160, 174, 192, 0.1)',
    badgeBg: 'rgba(160, 174, 192, 0.2)',
    textColor: '#4a5568',
    iconColor: 'text-gray-500',
  },
};

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StyledAccordion = styled(AccordionReactstrap)`
  .accordion-item {
    border: none;
    border-radius: 16px;
    background: white;
    box-shadow: ${({ isOpen, theme }) =>
      isOpen
        ? `0 8px 30px ${theme.shadowColor}`
        : '0 2px 10px rgba(0, 0, 0, 0.06)'};
    margin-bottom: 16px;
    transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: visible !important;
    z-index: ${({ isOpen }) => (isOpen ? '5' : '1')};
  }

  .accordion-item:focus-within {
    z-index: 10 !important;
  }

  .accordion-header {
    display: flex;
    background: ${({ isOpen, theme }) =>
      isOpen ? theme.gradient : theme.gradientLight};
    transition: background 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    border-radius: 16px 16px 0 0;
    z-index: 1;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${({ isOpen }) =>
        isOpen
          ? 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 100%)'
          : 'none'};
      pointer-events: none;
    }
  }

  .accordion-button {
    padding: 20px 24px;
    width: 100%;
    background-color: transparent !important;
    border: none;
    color: ${({ isOpen, theme }) => (isOpen ? 'white' : theme.textColor)};
    font-weight: ${({ isOpen }) => (isOpen ? '600' : '500')};
    position: relative;
    z-index: 1;

    &:focus {
      box-shadow: none;
    }

    &:hover {
      background-color: transparent !important;
    }
  }

  .accordion-button::after {
    display: none;
  }

  .accordion-body {
    padding: 24px;
    background: white;
    animation: ${({ isOpen }) => (isOpen ? slideDown : 'none')} 0.3s ease-out;
    position: relative;
    overflow: visible !important;
    border-radius: 0 0 16px 16px;
    z-index: 1;
  }

  .accordion-body .select2-selection__menu,
  .accordion-body .react-select__menu,
  .accordion-body .select__menu {
    z-index: 1040 !important;
  }
`;

// Icon wrapper with theme
const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ isOpen, theme }) =>
    isOpen ? 'rgba(255, 255, 255, 0.2)' : theme.iconBg};
  transition: all 0.3s ease;
  backdrop-filter: ${({ isOpen }) => (isOpen ? 'blur(4px)' : 'none')};
  flex-shrink: 0;
`;

// Status badge with theme
const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: ${({ isOpen, theme }) =>
    isOpen ? 'rgba(255, 255, 255, 0.2)' : theme.badgeBg || '#e5e7eb'};
  color: ${({ isOpen, theme }) => (isOpen ? 'white' : theme.textColor)};
  backdrop-filter: ${({ isOpen }) => (isOpen ? 'blur(4px)' : 'none')};
  border: ${({ isOpen }) =>
    isOpen ? '1px solid rgba(255,255,255,0.2)' : 'none'};
`;

// Toggle icon button - new styled component
const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: ${({ isOpen }) =>
    isOpen ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)'};
  color: ${({ isOpen, theme }) => (isOpen ? 'white' : theme.textColor)};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;

  &:hover {
    transform: scale(1.1);
    background: ${({ isOpen }) =>
      isOpen ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.08)'};
  }

  &:active {
    transform: scale(0.95);
  }

  i {
    font-size: 20px;
    transition: transform 0.3s ease;
  }
`;

// Export themes for use
export { THEMES };

// Updated Accordion component
export const Accordion = ({
  children,
  title,
  extraComponentBody,
  extraComponentHeader,
  id,
  extraTitle,
  className,
  defaultOpen = false,
  status = null,
  icon = 'bx bx-user',
  showBadge = true,
  theme = 'muted',
  customTheme = null,
}) => {
  const [isOpenAccordion, setIsOpenAccordion] = useState(
    defaultOpen ? '1' : '',
  );
  const { t } = useTranslation();

  // Get theme config
  const themeConfig = customTheme || THEMES[theme] || THEMES.primary;

  const statusConfig = {
    complete: {
      icon: 'bx bx-check-circle',
      label: 'Complete',
      color: '#10b981',
    },
    pending: { icon: 'bx bx-time', label: 'Pending', color: '#f59e0b' },
    error: { icon: 'bx bx-x-circle', label: 'Error', color: '#ef4444' },
    draft: { icon: 'bx bx-pencil', label: 'Draft', color: '#6b7280' },
  };

  const currentStatus = status ? statusConfig[status] : null;

  const isOpen = isOpenAccordion === '1';

  const toggleAccordion = () => {
    setIsOpenAccordion(isOpen ? '' : '1');
  };

  return (
    <StyledAccordion
      toggle={toggleAccordion}
      open={isOpenAccordion}
      className={className}
      flush
      isOpen={isOpen}
      theme={themeConfig}
    >
      <AccordionItem>
        <AccordionHeader
          id={id}
          targetId="1"
          isOpen={isOpen}
          theme={themeConfig}
        >
          <div className="w-100 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <IconWrapper isOpen={isOpen} theme={themeConfig}>
                <i
                  className={`${icon} text-xl ${
                    isOpen ? 'text-white' : themeConfig.iconColor
                  }`}
                ></i>
              </IconWrapper>

              <div className="d-flex flex-column">
                <h5
                  className="mb-0 font-semibold"
                  style={{
                    color: isOpen ? 'white' : themeConfig.textColor,
                  }}
                >
                  {t(title)}
                </h5>
                <div className="d-flex align-items-center gap-2">
                  {extraTitle && (
                    <span
                      className="text-xs opacity-75"
                      style={{
                        color: isOpen
                          ? 'rgba(255,255,255,0.8)'
                          : themeConfig.textColor,
                      }}
                    >
                      {extraTitle}
                    </span>
                  )}
                  {currentStatus && showBadge && (
                    <StatusBadge isOpen={isOpen} theme={themeConfig}>
                      <i className={`${currentStatus.icon} text-xs`}></i>
                      {currentStatus.label}
                    </StatusBadge>
                  )}
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-3">
              {extraComponentHeader}

              {/* Replace text with icon buttons */}
              <ToggleButton
                isOpen={isOpen}
                theme={themeConfig}
                onClick={toggleAccordion}
                aria-label={isOpen ? 'Collapse' : 'Expand'}
                type="button"
              >
                {isOpen ? (
                  <i className="bx bx-chevron-up"></i>
                ) : (
                  <i className="bx bx-chevron-down"></i>
                )}
              </ToggleButton>
            </div>
          </div>
        </AccordionHeader>

        {extraComponentBody}

        <AccordionBody accordionId="1" isOpen={isOpen}>
          {children}
        </AccordionBody>
      </AccordionItem>
    </StyledAccordion>
  );
};
